import { Check, ChevronDown, ChevronUp, Minus, Plus, Search } from 'lucide-react';

import {
  equipmentIcons,
  equipmentsTemplate,
  equipmentsTemplateEn,
  muscularGroupIcons,
  muscularGroupsTemplate,
  muscularGroupsTemplateEn,
} from '../../utils/templates';
import { estimateWorkoutMinutes } from '../../utils/workoutStats';

import { AI_WORKOUT, LIBRARY_ROWS, SCRATCH_WORKOUT, clock, formatLoad, formatNumber, unitFor, useLanding } from './landingData';
import { ExerciseArt, Pill, SectionHead, WhatLine } from './parts';

// The builder's tiles shown in the AI card, by their place in the app's templates (the same names and pictograms):
// back and biceps picked from biceps, shoulders, back, chest; dumbbells from bodyweight, dumbbells, kettlebell, door bar
const MUSCLE_TILES = [
  { index: 2, on: true },
  { index: 5 },
  { index: 6, on: true },
  { index: 9 },
];
const GEAR_TILES = [{ index: 0 }, { index: 2, on: true }, { index: 3 }, { index: 11 }];

// The templates are Title Case and go into the prompt as they are; on screen pt reads in sentence case, as in the app
const sentenceCase = (label) =>
  label.includes('/') ? label : label.replace(/(\s)(\p{Lu})(\p{Ll}+)/gu, (_, space, first, rest) => space + first.toLowerCase() + rest);

const sumSets = (workout) => workout.reduce((total, exercise) => total + exercise.sets, 0);
const minutesOf = (workout) => estimateWorkoutMinutes(workout.map((exercise) => ({ series: exercise.sets, interval: exercise.rest })));

const Tiles = ({ tiles, labels, icons, shown }) => (
  <ul className="lp-picks">
    {tiles.map(({ index, on }) => (
      <li key={index} className={`lp-pick${on ? ' is-on' : ''}`}>
        {on && (
          <span className="lp-pick__check">
            <Check />
          </span>
        )}
        <img src={icons[index]} alt="" width="52" height="52" loading="lazy" decoding="async" />
        <span>{shown(labels[index])}</span>
      </li>
    ))}
  </ul>
);

/** The AI card: the builder's three steps as the app draws them, then the workout it built, as Meus Treinos opens it */
const AiCard = () => {
  const { t, language, name } = useLanding();
  const b = (key, options) => t(`pages.Home.build.${key}`, options);
  const wb = (key, options) => t(`pages.Workout.builder.${key}`, options);
  const w = (key, options) => t(`pages.Workouts.view.${key}`, options);
  const isPt = language === 'pt';
  const shown = (label) => (isPt ? sentenceCase(label) : label);
  const sets = sumSets(AI_WORKOUT);

  return (
    <article className="lp-way">
      <h3 className="lp-way__title">
        {b('aiTitle')} <Pill kind="pro">{b('pro')}</Pill>
      </h3>
      <p className="lp-way__body">{b('aiBody')}</p>
      <div className="lp-way__ui" aria-hidden="true">
        <div className="lp-bld">
          <div className="lp-bld__head">
            <p className="lp-bld__title">{wb('aiTitle', { letter: 'B' })}</p>
            <span className="lp-stepper">
              <span className="lp-stepper__bars">
                <i />
                <i />
                <i />
              </span>
              {wb('stepOf', { n: 3, total: 3 })}
            </span>
          </div>
          <p className="lp-bld__q">{wb('musclesTitle')}</p>
          <Tiles
            tiles={MUSCLE_TILES}
            labels={isPt ? muscularGroupsTemplate : muscularGroupsTemplateEn}
            icons={muscularGroupIcons}
            shown={shown}
          />
          <p className="lp-bld__q">{wb('gearTitle')}</p>
          <Tiles tiles={GEAR_TILES} labels={isPt ? equipmentsTemplate : equipmentsTemplateEn} icons={equipmentIcons} shown={shown} />
          <p className="lp-bld__q">{wb('volumeTitle')}</p>
          <p className="lp-opt">
            <b>{t('components.ModalCreationAI.moderateVolume')}</b>
            <span>{t('components.ModalCreationAI.moderateVolumeDesc')}</span>
          </p>
          <p className="lp-bld__foot">
            <span className="lp-sbtn lp-sbtn--quiet">{wb('back')}</span>
            <span className="lp-sbtn lp-sbtn--go">
              {wb('generate')}
            </span>
          </p>
        </div>

        <div className="lp-res">
          <div className="lp-res__top">
            <span className="lp-letter">B</span>
            <span className="lp-res__names">
              <span className="lp-res__title">
                {b('aiWorkout')} <Pill kind="neutral">{t('pages.Home.demo.example')}</Pill>
              </span>
              <span className="lp-res__muscles">{b('aiMuscles')}</span>
            </span>
          </div>
          <dl className="lp-stats">
            <div>
              <dt>{w('statExercises', { count: AI_WORKOUT.length })}</dt>
              <dd>{AI_WORKOUT.length}</dd>
            </div>
            <div>
              <dt>{w('statSets', { count: sets })}</dt>
              <dd>{sets}</dd>
            </div>
            <div>
              <dt>{w('statMinutes')}</dt>
              <dd>~{minutesOf(AI_WORKOUT)}</dd>
            </div>
          </dl>
          <table className="lp-table">
            <thead>
              <tr>
                <th>
                  <span className="sr-only">{w('colArt')}</span>
                </th>
                <th>{w('colExercise')}</th>
                <th>{w('colSetsReps')}</th>
                <th>{w('colLoad')}</th>
              </tr>
            </thead>
            <tbody>
              {AI_WORKOUT.map((exercise) => (
                <tr key={exercise.file}>
                  <td>
                    <ExerciseArt file={exercise.file} />
                  </td>
                  <td>
                    <span className="lp-table__name">{name(exercise)}</span>
                    <WhatLine muscle={exercise.muscle} equipment={exercise.equipment} />
                  </td>
                  <td className="lp-table__num">{`${exercise.sets} × ${exercise.reps}`}</td>
                  <td className="lp-table__num">{formatLoad(exercise.load, language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </article>
  );
};

const Stepper = ({ label, value, unit }) => (
  <span className="lp-stp">
    <span className="lp-stp__label">{label}</span>
    <span className="lp-stp__box">
      <Minus />
      <b>
        {value}
        {unit && <small> {unit}</small>}
      </b>
      <Plus />
    </span>
  </span>
);

/** From scratch (free): the library pane filtered to legs, then the workout's exercises, one open on its steppers */
const ScratchCard = () => {
  const { t, language, name } = useLanding();
  const b = (key, options) => t(`pages.Home.build.${key}`, options);
  const lib = (key, options) => t(`pages.Workout.library.${key}`, options);
  const card = (key) => t(`components.ExerciseCard.${key}`);
  const unit = unitFor(language);
  const added = LIBRARY_ROWS.filter((row) => row.added).length;
  const sets = sumSets(SCRATCH_WORKOUT);
  const line = (exercise) =>
    `${exercise.sets} × ${exercise.reps} · ${formatLoad(exercise.load, language)} · ${t('pages.Workout.editor.restLine', { time: clock(exercise.rest) })}`;
  const [closed, open] = SCRATCH_WORKOUT;

  return (
    <article className="lp-way">
      <h3 className="lp-way__title">
        {b('zeroTitle')} <Pill kind="neutral">{b('free')}</Pill>
      </h3>
      <p className="lp-way__body">{b('zeroBody')}</p>
      <div className="lp-way__ui" aria-hidden="true">
        <div className="lp-lib">
          <p className="lp-lib__title">
            {lib('title')} <span>412</span>
          </p>
          <span className="lp-search">
            <Search />
            {lib('search')}
          </span>
          <span className="lp-chips">
            {/* Phones keep Todos, Peito and Pernas */}
            {['all', 'chest', 'back', 'shoulders', 'legs'].map((key) => (
              <span key={key} className={`lp-chip${key === 'legs' ? ' is-on' : ''}${key === 'back' || key === 'shoulders' ? ' lp-chip--wide' : ''}`}>
                {key === 'all' ? lib('all') : lib(`regionNames.${key}`)}
              </span>
            ))}
          </span>
          <ul className="lp-lib__rows">
            {LIBRARY_ROWS.map((row) => (
              <li key={row.file} className="lp-xrow">
                <ExerciseArt file={row.file} />
                <span className="lp-xrow__text">
                  <b>{name(row)}</b>
                  <WhatLine muscle={row.muscle} equipment={row.equipment} />
                </span>
                <span className={`lp-add${row.added ? ' is-on' : ''}`}>{row.added ? <Check /> : <Plus />}</span>
              </li>
            ))}
          </ul>
          <p className="lp-lib__foot">
            <span>{lib('inWorkout', { letter: 'A', count: added })}</span>
            <span className="lp-sbtn lp-sbtn--go">{lib('done')}</span>
          </p>
        </div>

        <div className="lp-edit">
          <p className="lp-edit__title">
            {t('pages.Workout.editor.exercises')}{' '}
            <span>{`${sets} ${t('pages.Workouts.view.statSets', { count: sets })} · ~${minutesOf(SCRATCH_WORKOUT)} min`}</span>
          </p>
          <div className="lp-erow">
            <ExerciseArt file={closed.file} />
            <span className="lp-erow__text">
              <b>{name(closed)}</b>
              <span>{line(closed)}</span>
            </span>
            <ChevronDown className="lp-erow__chev" />
          </div>
          <div className="lp-erow lp-erow--open">
            <ExerciseArt file={open.file} />
            <span className="lp-erow__text">
              <b>{name(open)}</b>
              <span>{line(open)}</span>
            </span>
            <ChevronUp className="lp-erow__chev" />
            <span className="lp-erow__fields">
              <Stepper label={card('series')} value={open.sets} />
              <Stepper label={card('reps')} value={open.reps} />
              <Stepper label={card('load')} value={formatNumber(open.load[unit], language)} unit={unit} />
              <Stepper label={card('rest')} value={clock(open.rest)} />
            </span>
          </div>
        </div>
      </div>
      <p className="lp-way__foot">{b('libraryFoot')}</p>
    </article>
  );
};

export const HomeBuild = () => {
  const { t } = useLanding();
  return (
    <section id="montar" className="lp-sec" aria-labelledby="lp-build-title">
      <div className="lp-wrap">
        <SectionHead id="lp-build-title" title={t('pages.Home.build.title')} sub={t('pages.Home.build.sub')} />
        <div className="lp-ways">
          <AiCard />
          <ScratchCard />
        </div>
      </div>
    </section>
  );
};
